import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ListSkeleton } from '../components/ui';
import {
  CheckSquare, Bell, Users, Radio, Bot,
  CalendarDays, ArrowRight, TrendingUp,
  CheckCircle2, Clock, AlertCircle, BookOpen,
  Building2, SlidersHorizontal, Plus, X, GripVertical,
  Maximize2, Minimize2, Check, RotateCcw, StickyNote,
} from 'lucide-react';
import api from '../api';
import { useToast } from '../context/ToastContext';
import { useIsMobile } from '../hooks/useResponsive';
import { WorkspacePrompt } from '../components/WorkspaceSwitch';

const STATO_COLOR = {
  attivo:   { bg:'var(--green-dim)',  color:'var(--green)',     dot:'#10b981' },
  inattivo: { bg:'var(--surface)',    color:'var(--text-muted)', dot:'#7878a0' },
  in_pausa: { bg:'var(--yellow-dim)', color:'var(--yellow)',    dot:'#f59e0b' },
};

function diffLabel(isoDate) {
  if (!isoDate) return null;
  const diff = Math.floor((new Date(isoDate) - new Date()) / 86400000);
  if (diff < 0)  return { label: `${Math.abs(diff)}g fa`,  color:'var(--red)' };
  if (diff === 0) return { label: 'Oggi',    color:'var(--yellow)' };
  if (diff === 1) return { label: 'Domani',  color:'var(--yellow)' };
  return           { label: `tra ${diff}g`, color:'var(--text-dim)' };
}

/* ── sub-components ──────────────────────────────────────────────────────── */
function StatCard({ Icon, label, value, color, dimBg }) {
  return (
    <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:'14px 16px', display:'flex', alignItems:'center', gap:12 }}>
      <div style={{ width:38, height:38, borderRadius:10, background: dimBg || 'var(--bg3)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
        <Icon size={17} color={color || 'var(--text-muted)'}/>
      </div>
      <div>
        <div style={{ fontSize:11, color:'var(--text-dim)', marginBottom:3, fontWeight:500 }}>{label}</div>
        <div style={{ fontSize:22, fontWeight:700, color: color || 'var(--text)', lineHeight:1 }}>{value}</div>
      </div>
    </div>
  );
}

function QuickLink({ Icon, label, to, accentColor }) {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate(to)} style={{
      display:'flex', alignItems:'center', gap:7, padding:'8px 14px',
      background:'var(--surface)', border:'1px solid var(--border)', borderRadius:9,
      cursor:'pointer', fontSize:12.5, fontWeight:500, color:'var(--text-muted)',
      transition:'all 0.15s', whiteSpace:'nowrap', touchAction:'manipulation',
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = accentColor || 'rgba(0,212,255,0.4)'; e.currentTarget.style.color='var(--text)'; e.currentTarget.style.background='var(--surface-h)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.color='var(--text-muted)'; e.currentTarget.style.background='var(--surface)'; }}>
      <Icon size={13} color={accentColor || 'var(--text-muted)'}/> {label}
    </button>
  );
}

function TaskRow({ task, onClick, isLast }) {
  const dot = task.priorita === 'alta' ? 'var(--red)' : task.priorita === 'media' ? 'var(--yellow)' : 'var(--border)';
  const scad = diffLabel(task.data_scadenza);
  return (
    <div onClick={onClick}
      style={{ display:'flex', alignItems:'center', gap:10, padding:'11px 14px',
        borderBottom: isLast ? 'none' : '1px solid var(--border)',
        cursor:'pointer', transition:'background 0.1s', touchAction:'manipulation' }}
      onMouseEnter={e => e.currentTarget.style.background='var(--surface-h)'}
      onMouseLeave={e => e.currentTarget.style.background='transparent'}>
      <div style={{ width:7, height:7, borderRadius:'50%', background:dot, flexShrink:0 }}/>
      <span style={{ fontSize:13, color:'var(--text)', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{task.titolo}</span>
      <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
        {task.cliente_nome && <span style={{ fontSize:10.5, color:'var(--text-dim)' }}>{task.cliente_nome}</span>}
        {scad && <span style={{ fontSize:10.5, color:scad.color, fontWeight:600 }}>{scad.label}</span>}
      </div>
    </div>
  );
}

function ScadenzaRow({ item, isLast }) {
  const navigate = useNavigate();
  const scad = diffLabel(item.data_scadenza);
  return (
    <div onClick={() => navigate('/scadenzario')}
      style={{ display:'flex', alignItems:'center', gap:10, padding:'11px 14px',
        borderBottom: isLast ? 'none' : '1px solid var(--border)',
        cursor:'pointer', transition:'background 0.1s', touchAction:'manipulation' }}
      onMouseEnter={e => e.currentTarget.style.background='var(--surface-h)'}
      onMouseLeave={e => e.currentTarget.style.background='transparent'}>
      {item.scaduto
        ? <AlertCircle size={13} color="var(--red)" style={{ flexShrink:0 }}/>
        : <Clock size={13} color={scad?.color || 'var(--text-dim)'} style={{ flexShrink:0 }}/>}
      <span style={{ fontSize:13, color:'var(--text)', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.titolo}</span>
      <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
        {(item.azienda || item.cliente_nome) && (
          <span style={{ fontSize:10.5, color:'var(--text-dim)' }}>{item.azienda || item.cliente_nome}</span>
        )}
        {scad && <span style={{ fontSize:10.5, color:scad.color, fontWeight:600 }}>{scad.label}</span>}
      </div>
    </div>
  );
}

function EmptyState({ Icon, msg, iconColor }) {
  return (
    <div style={{ padding:'28px 16px', textAlign:'center', color:'var(--text-dim)', fontSize:13, display:'flex', flexDirection:'column', alignItems:'center', gap:8 }}>
      <Icon size={20} color={iconColor || 'var(--text-dim)'}/>
      {msg}
    </div>
  );
}

function SkeletonRows() {
  return (
    <div style={{ padding:'14px 16px', display:'flex', flexDirection:'column', gap:10 }}>
      {[100,80,65].map(w => (
        <div key={w} style={{ height:13, borderRadius:6, background:'var(--border)', opacity:0.45, width:`${w}%` }}/>
      ))}
    </div>
  );
}

function ViewAllRow({ label, to }) {
  const navigate = useNavigate();
  return (
    <div onClick={() => navigate(to)}
      style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:5, padding:'9px 14px',
        fontSize:12, color:'var(--cyan)', cursor:'pointer', borderTop:'1px solid var(--border)', transition:'background 0.1s' }}
      onMouseEnter={e => e.currentTarget.style.background='var(--cyan-dim)'}
      onMouseLeave={e => e.currentTarget.style.background='transparent'}>
      {label} <ArrowRight size={12}/>
    </div>
  );
}

/* ── Widget: Nota personale (persistita su localStorage per utente) ───────── */
function NoteWidget({ userId }) {
  const storeKey = `eden-home-note-${userId || 'me'}`;
  const [text, setText] = useState(() => localStorage.getItem(`eden-home-note-${userId || 'me'}`) || '');
  useEffect(() => {
    const t = setTimeout(() => localStorage.setItem(storeKey, text), 400);
    return () => clearTimeout(t);
  }, [text, storeKey]);
  return (
    <textarea value={text} onChange={e => setText(e.target.value)}
      placeholder="Appunti personali… (salvati su questo dispositivo)"
      style={{ width:'100%', minHeight:120, height:'100%', background:'var(--bg3)', border:'1px solid var(--border)',
        borderRadius:10, color:'var(--text)', fontSize:13, padding:'12px', resize:'none', outline:'none',
        fontFamily:'inherit', lineHeight:1.6, boxSizing:'border-box' }}/>
  );
}

/* ── Registry widget disponibili ─────────────────────────────────────────── */
// w = colonne su 12 · h = 'normal' | 'tall'
const WIDGETS = {
  stats: {
    title: 'Statistiche', icon: TrendingUp, defaultW: 12,
    render: ({ stats, loading }) => (
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))', gap:10 }}>
        <StatCard Icon={CheckSquare} label="Task da fare"   value={loading?'…':stats?.task_oggi ?? 0}            color="var(--cyan)"   dimBg="var(--cyan-dim)"/>
        <StatCard Icon={Bell}        label="Notifiche"      value={loading?'…':stats?.notifiche_non_lette ?? 0} color="var(--yellow)" dimBg="var(--yellow-dim)"/>
        <StatCard Icon={Users}       label="Clienti attivi" value={loading?'…':stats?.clienti_attivi ?? 0}       color="var(--text-muted)" dimBg="var(--bg3)"/>
        <StatCard Icon={Radio}       label="Agenti attivi"  value={loading?'…':stats?.agenti_attivi ?? 0}        color="var(--green)"  dimBg="var(--green-dim)"/>
      </div>
    ),
  },
  quicklinks: {
    title: 'Accesso rapido', icon: ArrowRight, defaultW: 12,
    render: () => (
      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
        <QuickLink Icon={Users}        label="Clienti attivi" to="/clienti-attivi" accentColor="rgba(0,212,255,0.7)"/>
        <QuickLink Icon={CheckSquare}  label="Task"           to="/task"           accentColor="rgba(0,212,255,0.7)"/>
        <QuickLink Icon={CalendarDays} label="Scadenzario"    to="/scadenzario"    accentColor="var(--yellow)"/>
        <QuickLink Icon={TrendingUp}   label="Analytics"      to="/analytics"      accentColor="rgba(168,85,247,0.8)"/>
        <QuickLink Icon={BookOpen}     label="Schede Pep"     to="/pep-schede"     accentColor="var(--text-muted)"/>
        <QuickLink Icon={Building2}    label="Eden HQ"        to="/eden-hq"        accentColor="var(--text-muted)"/>
      </div>
    ),
  },
  tasks: {
    title: 'I tuoi task', icon: CheckSquare, defaultW: 6,
    render: ({ taskList, loading, navigate }) => (
      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
        {loading ? <SkeletonRows/> : taskList.length === 0
          ? <EmptyState Icon={CheckCircle2} msg="Nessun task in sospeso" iconColor="var(--green)"/>
          : (<>
              {taskList.map((t,i) => <TaskRow key={t.id} task={t} onClick={() => navigate(`/task?open=${t.id}`)} isLast={i===taskList.length-1}/>)}
              <ViewAllRow label="Vedi tutti i task" to="/task"/>
            </>)}
      </div>
    ),
  },
  scadenzario: {
    title: 'In scadenza — prossimi 7 giorni', icon: CalendarDays, defaultW: 6,
    render: ({ scadenzario }) => (
      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
        {scadenzario.length === 0
          ? <EmptyState Icon={CalendarDays} msg="Nessuna scadenza imminente"/>
          : (<>
              {scadenzario.map((t,i) => <ScadenzaRow key={t.id||i} item={t} isLast={i===scadenzario.length-1}/>)}
              <ViewAllRow label="Vedi scadenzario completo" to="/scadenzario"/>
            </>)}
      </div>
    ),
  },
  agenti: {
    title: 'Agenti dal vivo', icon: Bot, defaultW: 12,
    render: ({ agenti }) => (
      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
        {agenti.length === 0
          ? <EmptyState Icon={Bot} msg="Nessun agente"/>
          : (<>
              {agenti.map((a,i) => {
                const sc = STATO_COLOR[a.stato] || STATO_COLOR.inattivo;
                return (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'11px 14px', borderBottom: i<agenti.length-1?'1px solid var(--border)':'none' }}>
                    <div style={{ width:7, height:7, borderRadius:'50%', background:sc.dot, flexShrink:0 }}/>
                    <Bot size={13} color="var(--text-dim)" style={{ flexShrink:0 }}/>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:500, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.nome}</div>
                      {a.ruolo && <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:1 }}>{a.ruolo}</div>}
                    </div>
                    <span style={{ fontSize:10.5, background:sc.bg, color:sc.color, padding:'2px 8px', borderRadius:6, flexShrink:0 }}>
                      {a.stato==='attivo'?'Attivo':a.stato==='in_pausa'?'In pausa':'Inattivo'}
                    </span>
                  </div>
                );
              })}
              <ViewAllRow label="Vedi tutti gli agenti" to="/agenti-vivi"/>
            </>)}
      </div>
    ),
  },
  note: {
    title: 'Note personali', icon: StickyNote, defaultW: 6,
    render: ({ user }) => <NoteWidget userId={user?.id}/>,
  },
};

const DEFAULT_LAYOUT = [
  { key:'stats',       w:12, h:'normal' },
  { key:'quicklinks',  w:12, h:'normal' },
  { key:'tasks',       w:6,  h:'normal' },
  { key:'scadenzario', w:6,  h:'normal' },
  { key:'agenti',      w:12, h:'normal' },
];

const W_CYCLE = [4, 6, 8, 12]; // larghezze selezionabili (colonne su 12)

/* ── PAGE ────────────────────────────────────────────────────────────────── */
export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { success } = useToast();
  const isMobile = useIsMobile();
  const [stats, setStats]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [scadenzario, setScadenzario] = useState([]);
  const [layout, setLayout]         = useState(null);   // null = non ancora caricato
  const [edit, setEdit]             = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const loadedRef = useRef(false);
  const saveTimer = useRef(null);
  const dragFrom  = useRef(null);

  useEffect(() => {
    api.get('/home/stats')
      .then(r => { setStats(r.data); setLoading(false); })
      .catch(() => setLoading(false));
    api.get('/scadenzario', { params: { giorni: 7 } })
      .then(r => setScadenzario((r.data || []).slice(0, 6)))
      .catch(() => {});
    // Carica il layout personalizzato (o default)
    api.get('/user_dashboard', { silent: true })
      .then(r => {
        const cfg = r.data?.config;
        const valid = Array.isArray(cfg) && cfg.length
          ? cfg.filter(w => WIDGETS[w.key])
          : DEFAULT_LAYOUT;
        setLayout(valid);
      })
      .catch(() => setLayout(DEFAULT_LAYOUT));
  }, []);

  // Persistenza layout (debounced), saltando il primo set (caricamento)
  useEffect(() => {
    if (layout === null) return;
    if (!loadedRef.current) { loadedRef.current = true; return; }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put('/user_dashboard', { config: layout }, { silent: true }).catch(() => {});
    }, 600);
  }, [layout]);

  const ora    = new Date().getHours();
  const saluto = ora < 12 ? 'Buongiorno' : ora < 18 ? 'Buon pomeriggio' : 'Buonasera';
  const oggi   = new Date().toLocaleDateString('it-IT', { weekday:'long', day:'numeric', month:'long' });

  const taskList = stats?.task_list?.slice(0, 6) || [];
  const agenti   = stats?.agenti_lista || [];
  const ctx = { stats, loading, scadenzario, taskList, agenti, navigate, user };

  const setWidth   = (i, w) => setLayout(L => L.map((x, idx) => idx === i ? { ...x, w } : x));
  const toggleTall = (i)    => setLayout(L => L.map((x, idx) => idx === i ? { ...x, h: x.h === 'tall' ? 'normal' : 'tall' } : x));
  const remove     = (i)    => setLayout(L => L.filter((_, idx) => idx !== i));
  const addWidget  = (key)  => { setLayout(L => [...L, { key, w: WIDGETS[key].defaultW, h: 'normal' }]); setShowPicker(false); };
  const moveBy     = (i, d) => setLayout(L => {
    const j = i + d; if (j < 0 || j >= L.length) return L;
    const n = [...L]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });
  const reorder = (from, to) => setLayout(L => {
    if (from === to || from == null) return L;
    const n = [...L]; const [m] = n.splice(from, 1); n.splice(to, 0, m); return n;
  });
  const resetDefault = () => { setLayout(DEFAULT_LAYOUT); success('Home ripristinata'); };

  const cycleWidth = (i, cur) => {
    const idx = W_CYCLE.indexOf(cur);
    setWidth(i, W_CYCLE[(idx + 1) % W_CYCLE.length]);
  };

  const available = Object.keys(WIDGETS).filter(k => !(layout || []).some(w => w.key === k));

  return (
    <div className="eden-page">

      <WorkspacePrompt />

      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:22, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontSize:21, fontWeight:700, margin:'0 0 4px', color:'var(--text)' }}>
            {saluto}, {user?.nome}
          </h1>
          <div style={{ fontSize:12.5, color:'var(--text-muted)', textTransform:'capitalize' }}>{oggi}</div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          {edit && (
            <button onClick={resetDefault}
              style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:12, padding:'7px 12px', borderRadius:9, border:'1px solid var(--border)', background:'var(--surface)', color:'var(--text-muted)', cursor:'pointer', touchAction:'manipulation' }}>
              <RotateCcw size={13}/> Ripristina
            </button>
          )}
          <button onClick={() => { setEdit(e => !e); setShowPicker(false); if (edit) success('Layout salvato'); }}
            style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:12, fontWeight:600, padding:'7px 13px', borderRadius:9,
              border: edit ? '1px solid var(--cyan)' : '1px solid var(--border)',
              background: edit ? 'var(--cyan-dim)' : 'var(--surface)',
              color: edit ? 'var(--cyan)' : 'var(--text-muted)', cursor:'pointer', touchAction:'manipulation' }}>
            {edit ? <><Check size={14}/> Fine</> : <><SlidersHorizontal size={14}/> Personalizza</>}
          </button>
        </div>
      </div>

      {/* Banner modalità modifica */}
      {edit && (
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', background:'var(--cyan-dim)', border:'1px solid rgba(0,212,255,0.2)', borderRadius:10, padding:'10px 14px', marginBottom:16, fontSize:12.5, color:'var(--cyan)' }}>
          <SlidersHorizontal size={14}/>
          <span>Modalità personalizza: {isMobile ? 'usa ↑↓ per riordinare' : 'trascina per riordinare'}, regola larghezza/altezza, aggiungi o rimuovi widget. Le modifiche si salvano da sole.</span>
        </div>
      )}

      {/* Griglia widget */}
      {layout === null ? (
        <ListSkeleton rows={4} />
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(12, 1fr)', gap:16, alignItems:'start' }}>
          {layout.map((wcfg, i) => {
            const def = WIDGETS[wcfg.key];
            if (!def) return null;
            const span = isMobile ? 12 : (wcfg.w || def.defaultW || 6);
            const Icon = def.icon;
            const showHeader = edit || !['stats', 'quicklinks'].includes(wcfg.key);
            return (
              <div key={wcfg.key}
                draggable={edit && !isMobile}
                onDragStart={edit && !isMobile ? (e) => { dragFrom.current = i; e.dataTransfer.effectAllowed = 'move'; } : undefined}
                onDragOver={edit && !isMobile ? (e) => e.preventDefault() : undefined}
                onDrop={edit && !isMobile ? (e) => { e.preventDefault(); reorder(dragFrom.current, i); dragFrom.current = null; } : undefined}
                style={{
                  gridColumn: `span ${span}`,
                  border: edit ? '1px dashed rgba(0,212,255,0.4)' : '1px solid transparent',
                  borderRadius: 14, padding: edit ? 10 : 0,
                  background: edit ? 'rgba(0,212,255,0.03)' : 'transparent',
                  transition: 'border-color 0.15s',
                }}>
                {showHeader && (
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                    {edit && !isMobile && <GripVertical size={14} color="var(--text-dim)" style={{ cursor:'grab', flexShrink:0 }}/>}
                    {Icon && <Icon size={13} color="var(--text-dim)" style={{ flexShrink:0 }}/>}
                    <span style={{ fontSize:11, fontWeight:700, color:'var(--text-dim)', textTransform:'uppercase', letterSpacing:'0.06em' }}>{def.title}</span>

                    {edit && (
                      <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:4 }}>
                        {isMobile ? (
                          <>
                            <CtrlBtn title="Su" onClick={() => moveBy(i, -1)}>↑</CtrlBtn>
                            <CtrlBtn title="Giù" onClick={() => moveBy(i, +1)}>↓</CtrlBtn>
                          </>
                        ) : (
                          <CtrlBtn title="Larghezza" onClick={() => cycleWidth(i, wcfg.w || def.defaultW)}>
                            <Maximize2 size={12}/> {wcfg.w || def.defaultW}/12
                          </CtrlBtn>
                        )}
                        <CtrlBtn title="Altezza" active={wcfg.h === 'tall'} onClick={() => toggleTall(i)}>
                          {wcfg.h === 'tall' ? <Minimize2 size={12}/> : <Maximize2 size={12}/>}
                        </CtrlBtn>
                        <CtrlBtn title="Rimuovi" danger onClick={() => remove(i)}><X size={13}/></CtrlBtn>
                      </div>
                    )}
                  </div>
                )}
                <div style={{ minHeight: wcfg.h === 'tall' ? 320 : undefined, opacity: edit ? 0.92 : 1, pointerEvents: edit ? 'none' : 'auto' }}>
                  {def.render(ctx)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Aggiungi widget */}
      {edit && (
        <div style={{ marginTop:16 }}>
          {available.length === 0 ? (
            <div style={{ fontSize:12.5, color:'var(--text-dim)', textAlign:'center', padding:'12px' }}>Tutti i widget sono già in home.</div>
          ) : !showPicker ? (
            <button onClick={() => setShowPicker(true)}
              style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:7, padding:'12px', borderRadius:12, border:'2px dashed var(--border2)', background:'transparent', color:'var(--text-muted)', cursor:'pointer', fontSize:13, touchAction:'manipulation' }}
              onMouseEnter={e => e.currentTarget.style.borderColor='rgba(0,212,255,0.4)'}
              onMouseLeave={e => e.currentTarget.style.borderColor='var(--border2)'}>
              <Plus size={15}/> Aggiungi widget
            </button>
          ) : (
            <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:14 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--text-dim)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>Aggiungi un widget</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:8 }}>
                {available.map(k => {
                  const W = WIDGETS[k]; const WIcon = W.icon;
                  return (
                    <button key={k} onClick={() => addWidget(k)}
                      style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 12px', borderRadius:10, border:'1px solid var(--border)', background:'var(--bg3)', color:'var(--text)', cursor:'pointer', fontSize:13, textAlign:'left', touchAction:'manipulation' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor='rgba(0,212,255,0.4)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor='var(--border)'}>
                      {WIcon && <WIcon size={14} color="var(--cyan)"/>} {W.title}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* Pulsantino di controllo widget in modalità modifica */
function CtrlBtn({ children, onClick, title, danger, active }) {
  return (
    <button title={title} onClick={onClick}
      style={{ display:'inline-flex', alignItems:'center', gap:3, fontSize:11, fontWeight:600,
        padding:'3px 7px', borderRadius:6, cursor:'pointer', touchAction:'manipulation',
        border:`1px solid ${active ? 'var(--cyan)' : 'var(--border)'}`,
        background: active ? 'var(--cyan-dim)' : 'var(--surface)',
        color: danger ? 'var(--red)' : active ? 'var(--cyan)' : 'var(--text-muted)' }}>
      {children}
    </button>
  );
}
